/**
 * mediaworks805Catalog.js — 805 Media Works price list
 *
 * Ventura / Santa Barbara listing photo and film studio, the third column of
 * the quote builder's Competition analysis. Their site lists no prices; their
 * Aryeo order form (listings.805mediaworks.com/order-forms/5205a445-…) shows
 * them after the address step. Read 2026-11-04 from that form's products step,
 * which carries every size variant of every product, so one pass covers all
 * sizes. (The address step leaves a draft order on their side; a public address
 * was used, not ours or a client's.)
 *
 * Their four packages (Standard, Essential, Premium, Signature) and the
 * Photography Bundle are left out: the analysis compares line by line, never
 * against a package (Cyprian, 2026-11-04).
 *
 * Pricing models:
 * - `variants`: [min, max, price] size ranges, inclusive, as their form has
 *   them (a few overlap at the boundary, e.g. 3,000 sits in two ranges of
 *   their Standard package; the first match wins, as on their form). A size no
 *   range covers is not on their form at that size (over 10,000 sq ft for
 *   most, under 1,000 for Zillow).
 * - `flat`: one price at any size. `perImage`: `flat` per photo.
 * - `main` services stand alone; everything else is an add-on, which their
 *   form only sells beside a main service.
 */

export const MW_AS_OF = '2026-11-04';

// Their usual ladder: up to 2,000, then 2,001 to 3,000, … one price per range.
const THOUSANDS = (prices) => prices.map((price, i) => [i === 0 ? 0 : 1001 + i * 1000, 2000 + i * 1000, price]);

export const MW_PRODUCTS = [
  // Main services
  { id: 'mw_photo_hdr', name: 'Photography Only (HDR)', main: true,
    desc: 'Wide-angle HDR, their standard look. Aerial photos included.',
    variants: THOUSANDS([395, 475, 545, 605, 695, 775, 855, 955, 995]) },
  { id: 'mw_photo_flash', name: 'Photography Only (High-End Flash / Ambient)', main: true,
    desc: 'Flash and ambient, hand-blended. Aerial photos included.',
    variants: THOUSANDS([650, 700, 750, 800, 850, 900, 950, 1000, 1050]) },
  { id: 'mw_basic_video', name: 'Basic Videography or Reel Only', main: true,
    desc: 'A standard horizontal film OR a vertical reel. No drone.',
    variants: THOUSANDS([1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800]) },
  { id: 'mw_cinematic_film', name: 'Cinematic Property Film or Reel Only', main: true,
    desc: 'Premium film OR reel, drone, agent on camera and scripting included.',
    variants: [[0, 5000, 2750], [5001, 10000, 4250]] },
  { id: 'mw_signature_film', name: 'Signature Property Film or Reel', main: true,
    desc: 'Full or two-day shoot: twilight, day-to-night, timelapse, agent on camera.',
    variants: [[0, 5000, 3805], [5001, 10000, 5805]] },
  { id: 'mw_fpv_tour', name: 'FPV Drone Tour', main: true, flat: 1500,
    desc: 'Horizontal and vertical full tours plus 2 short reels.' },
  // Add-ons
  { id: 'mw_reel_booster', name: 'Social Reel Booster Pack', flat: 600,
    desc: '4 extra social videos cut from the shoot footage.' },
  { id: 'mw_zillow', name: '3D Zillow Showcase',
    variants: [[1000, 1999, 375], [2000, 2999, 400], [3000, 3999, 450], [4000, 4999, 500], [5000, 5999, 550], [6000, 6999, 650], [7000, 7999, 750], [8000, 8999, 850]] },
  { id: 'mw_matterport', name: '3D Matterport Virtual Tour',
    variants: THOUSANDS([299, 399, 499, 599, 699, 799, 899, 999, 1099]) },
  { id: 'mw_agent_script', name: 'Agent On-Camera + Script', flat: 250 },
  { id: 'mw_headshots', name: 'Agent Headshots / Lifestyle', flat: 250 },
  { id: 'mw_guided_walkthrough', name: 'Guided Walkthrough', flat: 250 },
  { id: 'mw_one_take', name: 'Vertical "One-Take" Video', flat: 399,
    desc: 'A one-take vertical walkthrough of the property.' },
  { id: 'mw_virtual_twilight', name: 'Virtual Twilight Photo', flat: 20, perImage: true },
  { id: 'mw_virtual_staging', name: 'Virtual Staging', flat: 50, perImage: true },
  { id: 'mw_twilight_video', name: 'Twilight Video', flat: 350 },
  { id: 'mw_community_broll', name: 'Local Community Video B-Roll', flat: 250 },
  { id: 'mw_website_domain', name: 'Custom Website Domain', flat: 50 },
  { id: 'mw_twilight_photos', name: 'Twilight Photos', flat: 250 },
  { id: 'mw_fp_2d', name: '2D Floor Plan', flat: 85 },
  { id: 'mw_fp_3d', name: '3D Floor Plan', flat: 115 },
  { id: 'mw_amenities', name: 'Amenities Shoot', flat: 150 },
  { id: 'mw_agent_broll', name: 'Social Media Agent B-Roll', flat: 250 },
  { id: 'mw_site_plan', name: 'Rendered 2D Site Plan',
    variants: THOUSANDS([250, 300, 350, 400, 450, 500, 550]) },
  { id: 'mw_aerial_photos', name: 'Aerial Drone Photos', flat: 200 },
  { id: 'mw_aerial_video', name: 'Aerial Drone Video', flat: 200 },
];

export function findMwProduct(id) {
  return MW_PRODUCTS.find((p) => p.id === id) ?? null;
}

/** Their price for one unit at a size, or null where their form has none. */
export function mwGetPrice(product, sqft = 0) {
  if (product.flat !== undefined) return product.flat;
  const n = Math.floor(Number(sqft) || 0);
  const hit = product.variants.find(([min, max]) => n >= min && n <= max);
  return hit ? hit[2] : null;
}

/** The size range a typical ladder of theirs prices a square footage in. */
export function mwRangeLabel(sqft = 0) {
  const n = Math.floor(Number(sqft) || 0);
  if (n <= 2000) return 'Up to 2,000 sq ft';
  if (n > 10000) return 'Over 10,000 sq ft: not on their form';
  const hi = Math.ceil(n / 1000) * 1000;
  return `${(hi - 999).toLocaleString('en-US')} – ${hi.toLocaleString('en-US')} sq ft`;
}
